import { useEffect, useState } from "react"
import { Modal, Form, Input, Select, DatePicker } from "antd"
import { Toast } from "@/components/Toast"
import {
  TrainingPlanModalProps,
  TrainingPlanFormData,
  TrainingPlanSaveData,
} from "../types"
import trainingProjectApi from "@/pages/domain/training-project/api"
import domainLv2Api from "@/pages/domain/domain-lv2/api"
import { TrainingProject } from "@/pages/domain/training-project/types"
import { DomainLv2 } from "@/pages/domain/domain-lv2/types"
import dayjs, { Dayjs } from "dayjs"
import { ID } from "@/types/common"
import { useDomainLv1 } from "@/hooks/useDomainLv1"

const { Option } = Select
const { TextArea } = Input

// 内部表单数据类型，用于处理Dayjs对象
interface InternalFormData {
  studentId?: ID
  domainId?: ID
  domainLv2Id?: ID
  trainingProjectId?: ID
  trainingProjectIds?: ID[]
  note?: string
  expectedBeginDate?: Dayjs
  expectedEndDate?: Dayjs
  actualBeginDate?: Dayjs
  actualEndDate?: Dayjs
}

const TrainingPlanModal = ({
  isOpen,
  onClose,
  onSave,
  trainingPlan,
  loading = false,
  students,
  studentsLoading,
  initialValues,
}: TrainingPlanModalProps) => {
  // 表单
  const [form] = Form.useForm<InternalFormData>()
  // 使用全局一级领域数据
  const { domainLv1s, isLoading: domainLv1sLoading } = useDomainLv1()
  // 二级领域列表
  const [subDomains, setSubDomains] = useState<DomainLv2[]>([])
  // 二级领域列表加载状态
  const [subDomainsLoading, setSubDomainsLoading] = useState(false)
  // 训练项目列表
  const [trainingProjects, setTrainingProjects] = useState<TrainingProject[]>(
    []
  )
  // 训练项目列表加载状态
  const [trainingProjectsLoading, setTrainingProjectsLoading] = useState(false)

  // 加载二级领域列表（根据一级领域ID）
  const loadSubDomains = async (domainId: ID) => {
    try {
      setSubDomainsLoading(true)
      const res = await domainLv2Api.getList(domainId)
      setSubDomains(res.data || [])
    } catch (error) {
      Toast.error("加载二级领域数据失败")
      console.error("加载二级领域数据失败:", error)
    } finally {
      setSubDomainsLoading(false)
    }
  }

  // 加载训练项目列表（根据二级领域ID）
  const loadTrainingProjects = async (domainLv2Id: ID) => {
    try {
      setTrainingProjectsLoading(true)
      const res = await trainingProjectApi.getList(domainLv2Id)
      setTrainingProjects(res.data || [])
    } catch (error) {
      Toast.error("加载训练项目列表失败")
      console.error("加载训练项目列表失败:", error)
    } finally {
      setTrainingProjectsLoading(false)
    }
  }

  // 处理一级领域变化
  const handleDomainChange = (domainId: ID) => {
    // 加载对应的二级领域
    loadSubDomains(domainId)
    // 清空二级领域和训练项目选择
    form.setFieldsValue({
      domainLv2Id: undefined,
      trainingProjectId: undefined,
      trainingProjectIds: [],
    })
  }

  // 处理二级领域变化
  const handleSubDomainChange = (domainLv2Id: ID) => {
    // 加载对应的训练项目
    loadTrainingProjects(domainLv2Id)
    // 清空训练项目选择
    form.setFieldsValue({
      trainingProjectId: undefined,
      trainingProjectIds: [],
    })
  }

  // 转换内部表单数据为API格式（新增/编辑）
  const convertToTrainingPlanFormat = (
    data: InternalFormData
  ): TrainingPlanSaveData => {
    const basePayload = {
      studentId: data.studentId!,
      note: data.note?.trim(),
      expectedBeginDate: data.expectedBeginDate?.format("YYYY-MM-DD"),
      expectedEndDate: data.expectedEndDate?.format("YYYY-MM-DD"),
      actualBeginDate: data.actualBeginDate?.format("YYYY-MM-DD"),
      actualEndDate: data.actualEndDate?.format("YYYY-MM-DD"),
    }

    if (trainingPlan) {
      return {
        ...basePayload,
        trainingProjectId: data.trainingProjectId!,
      } as TrainingPlanFormData
    }

    return {
      ...basePayload,
      trainingProjectIds: data.trainingProjectIds || [],
    }
  }

  // 编辑模式：填充现有数据
  useEffect(() => {
    if (!isOpen) return
    form.setFieldsValue({
      studentId: initialValues.studentId,
      domainId: initialValues.domainId,
      domainLv2Id: initialValues.domainLv2Id,
    })
    if (trainingPlan) {
      // 编辑模式：通过 trainingProjectId 反向查找 domainId 和 domainLv2Id
      const loadEditData = async () => {
        try {
          // 1. 通过 trainingProjectId 获取训练项目信息，得到 domainLv2Id
          const trainingProject =
            await trainingProjectApi.getById(
              trainingPlan.trainingProjectId
            )
          if (!trainingProject) {
            Toast.error("加载训练项目信息失败")
            return
          }

          // 2. 通过 domainLv2Id 获取二级领域信息，得到 domainId
          const subdomain = await domainLv2Api.getById(
            trainingProject.domainLv2Id
          )
          if (!subdomain) {
            Toast.error("加载二级领域信息失败")
            return
          }

          // 3. 加载对应的二级领域列表和训练项目列表
          await Promise.all([
            loadSubDomains(subdomain.domainLv1Id),
            loadTrainingProjects(trainingProject.domainLv2Id),
          ])

          // 4. 设置表单值
          form.setFieldsValue({
            domainId: subdomain.domainLv1Id,
            domainLv2Id: trainingProject.domainLv2Id,
            trainingProjectId: trainingPlan.trainingProjectId,
            note: trainingPlan.note,
            expectedBeginDate: trainingPlan.expectedBeginDate
              ? dayjs(trainingPlan.expectedBeginDate)
              : undefined,
            expectedEndDate: trainingPlan.expectedEndDate
              ? dayjs(trainingPlan.expectedEndDate)
              : undefined,
            actualBeginDate: trainingPlan.actualBeginDate
              ? dayjs(trainingPlan.actualBeginDate)
              : undefined,
            actualEndDate: trainingPlan.actualEndDate
              ? dayjs(trainingPlan.actualEndDate)
              : undefined,
          })
        } catch (error) {
          console.error("加载下拉选项数据失败:", error)
          Toast.error("加载编辑数据失败")
        }
      }
      loadEditData()
    } else {
      const loadCreateDefaults = async () => {
        try {
          if (initialValues.domainId) {
            await loadSubDomains(initialValues.domainId)
          } else {
            setSubDomains([])
          }
          if (initialValues.domainLv2Id) {
            await loadTrainingProjects(initialValues.domainLv2Id)
          } else {
            setTrainingProjects([])
          }
        } catch (error) {
          console.error("加载新增默认筛选数据失败:", error)
        }
      }
      loadCreateDefaults()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // 处理表单提交
  const handleSubmit = async () => {
    const values = await form.validateFields()
    const formData = convertToTrainingPlanFormat(values)
    await onSave(formData)
  }

  // 处理取消
  const handleCancel = () => {
    onClose()
  }

  return (
    <Modal
      title={trainingPlan ? "编辑训练计划" : "新增训练计划"}
      open={isOpen}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={700}
      destroyOnHidden
      okText="确定"
      cancelText="取消"
    >
      <Form form={form} layout="vertical" preserve={false}>
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label="学生"
            name="studentId"
            rules={[{ required: true, message: "请选择学生" }]}
          >
            <Select
              placeholder="请选择学生"
              loading={studentsLoading}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  ?.toLowerCase()
                  ?.includes(input.toLowerCase())
              }
            >
              {students.map((student) => (
                <Option key={student.id} value={student.id}>
                  {student.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label="一级领域"
            name="domainId"
            rules={[{ required: true, message: "请选择一级领域" }]}
          >
            <Select
              placeholder="请选择一级领域"
              loading={domainLv1sLoading}
              onChange={handleDomainChange}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  ?.toLowerCase()
                  ?.includes(input.toLowerCase())
              }
            >
              {domainLv1s.map((domain) => (
                <Option key={domain.id} value={domain.id}>
                  {domain.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="二级领域"
            name="domainLv2Id"
            rules={[{ required: true, message: "请选择二级领域" }]}
          >
            <Select
              placeholder="请先选择一级领域"
              loading={subDomainsLoading}
              disabled={subDomains.length === 0}
              onChange={handleSubDomainChange}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  ?.toLowerCase()
                  ?.includes(input.toLowerCase())
              }
            >
              {subDomains.map((subdomain) => (
                <Option key={subdomain.id} value={subdomain.id}>
                  {subdomain.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label="训练项目"
            name={trainingPlan ? "trainingProjectId" : "trainingProjectIds"}
            rules={[
              trainingPlan
                ? { required: true, message: "请选择训练项目" }
                : {
                    required: true,
                    message: "请选择训练项目（可多选）",
                    type: "array",
                  },
            ]}
          >
            <Select
              placeholder="请先选择二级领域"
              loading={trainingProjectsLoading}
              disabled={trainingProjects.length === 0}
              mode={trainingPlan ? undefined : "multiple"}
              maxTagCount="responsive"
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  ?.toLowerCase()
                  ?.includes(input.toLowerCase())
              }
            >
              {trainingProjects.map((project) => (
                <Option key={project.id} value={project.id}>
                  {project.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item label="计划开始日期" name="expectedBeginDate">
            <DatePicker
              placeholder="请选择计划开始日期"
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
            />
          </Form.Item>

          <Form.Item label="计划结束日期" name="expectedEndDate">
            <DatePicker
              placeholder="请选择计划结束日期"
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
            />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item label="实际开始日期" name="actualBeginDate">
            <DatePicker
              placeholder="请选择实际开始日期"
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
            />
          </Form.Item>

          <Form.Item label="实际结束日期" name="actualEndDate">
            <DatePicker
              placeholder="请选择实际结束日期"
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
            />
          </Form.Item>
        </div>

        <Form.Item label="备注" name="note">
          <TextArea
            placeholder="请输入备注（可选）"
            rows={4}
            maxLength={200}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default TrainingPlanModal
